import { BALANCE, REFERENCE_SOLVE_MS } from '../balance.ts';
import { edgeBetween, findRoutes, getMap, hasRoom, isRopeLink, roomDef } from '../maps/graph.ts';
import type { MapDef, MapShape } from '../maps/types.ts';
import type {
  ActionError,
  EntranceId,
  GameState,
  GearId,
  Plan,
  PlayerId,
  RoomId,
} from '../types.ts';
import { emit, fail, mustPlayer, type Ctx } from './context.ts';
import { startExecution } from './execution.ts';

const ENTRANCES: readonly EntranceId[] = ['roof', 'sewer', 'service'];
const GEAR: readonly GearId[] = ['proPick', 'jammer', 'rope', 'disguise', 'drone', 'emp'];

/** What a plan needs from the gear budget. */
export function gearCost(gear: readonly GearId[]): number {
  return gear.reduce((sum, g) => sum + BALANCE.gearCost[g], 0);
}

/** Number of rope hops a route takes (adjacent rooms joined only by a rope link). */
export function ropeHops(map: MapShape, route: readonly RoomId[]): number {
  let n = 0;
  for (let i = 0; i + 1 < route.length; i++) {
    const a = route[i] as RoomId;
    const b = route[i + 1] as RoomId;
    if (!edgeBetween(map, a, b) && isRopeLink(map, a, b)) n++;
  }
  return n;
}

/** Kind of a room of a map known to have it. */
const kindOf = (map: MapShape, id: RoomId): string | undefined =>
  map.rooms.find((r) => r.id === id)?.kind;

/** Why a plan is not allowed, or `null` if it is (GDD §5.2). */
export function validatePlan(map: MapShape, plan: Plan): string | null {
  if (!ENTRANCES.includes(plan.entrance)) return 'unknown entrance';
  const route = plan.route;
  if (!Array.isArray(route)) return 'the route must be a list of rooms';
  if (route.length < BALANCE.routeMinRooms || route.length > BALANCE.routeMaxRooms) {
    return `a route has ${BALANCE.routeMinRooms}–${BALANCE.routeMaxRooms} rooms`;
  }
  if (new Set(route).size !== route.length) return 'a room appears twice on the route';
  for (const r of route) {
    if (!hasRoom(map, r)) return `unknown room ${r}`;
    if (kindOf(map, r) === 'optional') return `${r} is an alcove, not part of the route`;
  }
  if (route[0] !== map.entrances[plan.entrance]) return 'the route must start at the entrance room';
  if (route[route.length - 1] !== map.vault) return 'the route must end at the vault';
  for (let i = 0; i + 1 < route.length; i++) {
    const a = route[i] as RoomId;
    const b = route[i + 1] as RoomId;
    if (!edgeBetween(map, a, b) && !isRopeLink(map, a, b)) return `${a} and ${b} are not connected`;
  }
  const hops = ropeHops(map, route);
  if (hops > 1) return 'one rope allows one hop';
  if (hops === 1 && !plan.gear.includes('rope')) return 'a rope hop needs the Rope';

  if (!Array.isArray(plan.gear)) return 'gear must be a list';
  if (new Set(plan.gear).size !== plan.gear.length) return 'each piece of gear at most once';
  if (plan.gear.some((g) => !GEAR.includes(g))) return 'unknown gear';
  if (gearCost(plan.gear) > BALANCE.gearBudget) return `gear costs more than ${BALANCE.gearBudget}`;

  if (!Array.isArray(plan.detours)) return 'detours must be a list';
  if (plan.detours.length > BALANCE.maxDetours) return `at most ${BALANCE.maxDetours} detours`;
  const alcoves = new Set<RoomId>();
  for (const d of plan.detours) {
    if (!route.includes(d.anchor)) return `${d.anchor} is not on the route`;
    if (!hasRoom(map, d.room) || kindOf(map, d.room) !== 'optional') {
      return `${d.room} is not an alcove`;
    }
    if (!edgeBetween(map, d.anchor, d.room)) return `${d.room} does not hang off ${d.anchor}`;
    if (alcoves.has(d.room)) return `${d.room} appears twice`;
    alcoves.add(d.room);
  }
  return null;
}

/** Rough time (ms) a room costs: used for the emergency plan and by bots (GDD §8, [T]). */
export function roomEstimateMs(map: MapDef, room: RoomId): number {
  const pool = roomDef(map, room).pool;
  const mean = pool.reduce((s, t) => s + REFERENCE_SOLVE_MS[t], 0) / pool.length;
  return Math.round(mean * 1.35);
}

export function estimatePlanMs(map: MapDef, plan: Plan): number {
  let total = 0;
  const route = plan.route;
  route.forEach((room, i) => {
    total += room === map.vault ? REFERENCE_SOLVE_MS.vault : roomEstimateMs(map, room);
    const next = route[i + 1];
    if (next) {
      const e = edgeBetween(map, room, next);
      if (e?.guarded) total += REFERENCE_SOLVE_MS.corridor;
      else if (!e) total += BALANCE.ropeHopMs;
    }
  });
  for (const d of plan.detours) {
    total += roomEstimateMs(map, d.room) + BALANCE.detourReturnMs;
    if (edgeBetween(map, d.anchor, d.room)?.guarded) total += REFERENCE_SOLVE_MS.corridor;
  }
  total += REFERENCE_SOLVE_MS.escape;
  if (plan.entrance === 'roof') total += BALANCE.roofTimerCostMs;
  return total;
}

/** Applied when nobody proposes anything in time: sewers, quickest route, nothing bought. */
export function emergencyPlan(s: GameState): Plan {
  const map = getMap(s.config.mapId);
  const routes = findRoutes(map, 'sewer', { allowRope: false });
  const best = routes.reduce((a, b) =>
    estimatePlanMs(map, { entrance: 'sewer', route: b, detours: [], gear: [] }) <
    estimatePlanMs(map, { entrance: 'sewer', route: a, detours: [], gear: [] })
      ? b
      : a,
  );
  return { entrance: 'sewer', route: best, detours: [], gear: [] };
}

function votes(s: GameState): { yes: number; no: number; cast: number } {
  const v = Object.values(s.planning.votes);
  const yes = v.filter(Boolean).length;
  return { yes, no: v.length - yes, cast: v.length };
}

function approve(
  ctx: Ctx,
  plan: Plan,
  how: 'vote' | 'tiebreak' | 'alone' | 'emergency' | 'timeout',
): void {
  const p = ctx.s.planning;
  p.approved = plan;
  p.substage = 'done';
  p.proposal = null;
  emit(ctx, 'plan.approved', { plan, how });
  startExecution(ctx, plan);
}

function reject(ctx: Ctx): void {
  const p = ctx.s.planning;
  p.rejections += 1;
  p.proposal = null;
  p.votes = {};
  p.substage = 'awaiting';
  p.stageStartedMs = ctx.s.clockMs;
  emit(ctx, 'plan.rejected', { rejections: p.rejections });
}

// ---- Actions ---------------------------------------------------------------------------

export function propose(ctx: Ctx, by: PlayerId, plan: Plan): ActionError | null {
  const s = ctx.s;
  const p = s.planning;
  if (mustPlayer(s, by).role !== 'mastermind')
    return fail('notAllowed', 'only the mastermind proposes');
  if (p.substage !== 'awaiting') return fail('wrongPhase', 'a proposal is already on the table');
  const problem = validatePlan(getMap(s.config.mapId), plan);
  if (problem) return fail('invalid', problem);
  const clean: Plan = structuredClone(plan);
  if (p.rejections >= BALANCE.maxRejections) {
    // After two rejections the mastermind decides alone (GDD §5.2).
    p.decidedAlone = true;
    approve(ctx, clean, 'alone');
    return null;
  }
  p.proposal = clean;
  p.votes = {};
  p.substage = 'voting';
  p.stageStartedMs = s.clockMs;
  emit(ctx, 'plan.proposed', { plan: clean });
  return null;
}

export function vote(ctx: Ctx, by: PlayerId, yes: boolean): ActionError | null {
  const s = ctx.s;
  const p = s.planning;
  if (p.substage !== 'voting') return fail('wrongPhase', 'there is nothing to vote on');
  if (by in p.votes) return fail('invalid', 'you already voted');
  p.votes[by] = yes;
  emit(ctx, 'plan.voted', { by, yes });
  if (Object.keys(p.votes).length >= s.players.length) resolveVoting(ctx, false);
  return null;
}

/** `final` = the overall planning clock ran out: missing votes are not counted as "no". */
function resolveVoting(ctx: Ctx, final: boolean): void {
  const s = ctx.s;
  const p = s.planning;
  const plan = p.proposal as Plan;
  const { yes, no, cast } = votes(s);
  const noTotal = final ? no : no + (s.players.length - cast);
  emit(ctx, 'plan.voteResult', { yes, no: noTotal });
  if (yes > noTotal) approve(ctx, plan, 'vote');
  else if (yes < noTotal) {
    reject(ctx);
    if (final) approve(ctx, emergencyPlan(s), 'emergency');
  } else if (final) approve(ctx, plan, 'timeout');
  else {
    p.substage = 'tiebreak';
    p.stageStartedMs = s.clockMs;
    emit(ctx, 'plan.tie', {});
  }
}

export function tiebreak(
  ctx: Ctx,
  by: PlayerId,
  decision: 'approve' | 'revise',
): ActionError | null {
  const s = ctx.s;
  const p = s.planning;
  if (mustPlayer(s, by).role !== 'mastermind')
    return fail('notAllowed', 'only the mastermind breaks ties');
  if (p.substage !== 'tiebreak') return fail('wrongPhase', 'there is no tie');
  if (decision === 'approve') approve(ctx, p.proposal as Plan, 'tiebreak');
  else reject(ctx);
  return null;
}

// ---- Time ------------------------------------------------------------------------------

export function tickPlanning(ctx: Ctx): void {
  const s = ctx.s;
  const p = s.planning;
  if (s.phase !== 'planning') return;
  const planningOver = s.clockMs - s.phaseStartedMs >= BALANCE.planningMs;
  if (planningOver) {
    if (p.substage === 'awaiting') approve(ctx, emergencyPlan(s), 'emergency');
    else if (p.substage === 'voting') resolveVoting(ctx, true);
    else if (p.substage === 'tiebreak') approve(ctx, p.proposal as Plan, 'timeout');
    return;
  }
  if (p.substage === 'voting' && s.clockMs - p.stageStartedMs >= BALANCE.planVoteMs) {
    resolveVoting(ctx, false);
  } else if (p.substage === 'tiebreak' && s.clockMs - p.stageStartedMs >= BALANCE.planVoteMs) {
    approve(ctx, p.proposal as Plan, 'timeout');
  }
}
